import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsNative } from '~/context/common/DeviceContext';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import { BrowserCookies } from '~/utilities';
import { logInfo } from '~/utilities/loggers';
import useAccountModalReady from './useAccountModalReady';

const ACCOUNT_MODAL_READY_TIMEOUT_MS = 5000;

type QueuedAction = () => void;

/**
 * Hook to queue account modal actions that occur before the modal is ready.
 * Returns an executor function that either runs immediately (if ready) or queues the action,
 * plus a loading state indicating we're waiting for the modal to become ready.
 *
 * Single source of truth for queueing logic:
 * - Logged-in users: execute immediately, no queueing
 * - Native app users: execute immediately, no queueing
 * - Logged-out web users: queue if modal not ready, show loading state
 *
 * Use case: Prevents dead clicks when users interact with account modal triggers
 * before the modal MFE has loaded.
 */
export default function useQueuedAccountModalAction() {
  const correlationId = useEventCorrelationId();
  const isAccountModalReady = useAccountModalReady();
  const isLoggedIn = useIsLoggedIn();
  const isNative = useIsNative();
  const [isWaitingForReady, setIsWaitingForReady] = useState(false);
  const queuedActionRef = useRef<QueuedAction | null>(null);
  const shouldQueue = !isLoggedIn && !isNative;
  const isAccountModalReadyRef = useRef(isAccountModalReady);
  const latestDiagnosticRef = useRef({ isLoggedIn, isNative, shouldQueue });

  // Keep refs updated with current state
  useEffect(() => {
    isAccountModalReadyRef.current = isAccountModalReady;
  }, [isAccountModalReady]);

  useEffect(() => {
    latestDiagnosticRef.current = { isLoggedIn, isNative, shouldQueue };
  }, [isLoggedIn, isNative, shouldQueue]);

  // Fire a DD diagnostic action if the account modal ready event never arrives
  useEffect(() => {
    if (!isWaitingForReady) return;

    // Snapshot from the render that set isWaitingForReady — effectively click time in practice
    const {
      isLoggedIn: isLoggedInAtClick,
      isNative: isNativeAtClick,
      shouldQueue: shouldQueueAtClick
    } = latestDiagnosticRef.current;

    const timeout = setTimeout(() => {
      const resourceEntries = performance.getEntriesByType(
        'resource'
      ) as PerformanceResourceTiming[];

      const getManifestDiagnostics = (
        entry: PerformanceResourceTiming | undefined
      ) => ({
        durationMs: entry ? Math.round(entry.duration) : null,
        found: !!entry,
        status: entry
          ? 'responseStatus' in entry
            ? (entry as PerformanceResourceTiming & { responseStatus: number })
                .responseStatus
            : null
          : null,
        transferSize: entry?.transferSize ?? null
      });

      const accountModalManifestEntry = resourceEntries.findLast(e =>
        e.name.includes('authentication-flows/account-modal/assets-manifest')
      );
      const headerManifestEntry = resourceEntries.findLast(e =>
        e.name.includes('header-module/header/assets-manifest')
      );

      logInfo('vdp-get-started-loading-freeze-timeout', {
        accountModalMfe: {
          isDomPresent: !!document.querySelector(
            '[data-testid="mfer-CoreAccountModal-default"]'
          ),
          manifest: getManifestDiagnostics(accountModalManifestEntry)
        },
        browserCookieId: BrowserCookies.get('BrowserCookieId'),
        correlationId,
        headerMfe: {
          // data-mfe-theme is on the MFE's <header> but absent from the skeleton <div>
          isDomPresent: !!document.querySelector(
            '#core-header-ui[data-mfe-theme]'
          ),
          manifest: getManifestDiagnostics(headerManifestEntry)
        },
        isAccountModalReadyAtTimeout: isAccountModalReadyRef.current,
        isLoggedInAtClick,
        isLoggedInAtTimeout: latestDiagnosticRef.current.isLoggedIn,
        isNativeAtClick,
        isNativeAtTimeout: latestDiagnosticRef.current.isNative,
        shouldQueueAtClick,
        shouldQueueAtTimeout: latestDiagnosticRef.current.shouldQueue,
        timeoutMs: ACCOUNT_MODAL_READY_TIMEOUT_MS,
        url: window.location.href
      });
    }, ACCOUNT_MODAL_READY_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [correlationId, isWaitingForReady]);

  // Process queued action when modal becomes ready
  useEffect(() => {
    if (isAccountModalReady && queuedActionRef.current) {
      const action = queuedActionRef.current;
      queuedActionRef.current = null;

      try {
        action();
      } finally {
        setIsWaitingForReady(false);
      }
    }
  }, [isAccountModalReady]);

  const executeOrQueue = useCallback(
    (action: QueuedAction) => {
      // Logged-in or native users: always execute immediately
      if (!shouldQueue) {
        action();

        return;
      }

      // Logged-out web users: queue if modal not ready
      // Read from ref to get current value, not stale closure value
      if (isAccountModalReadyRef.current) {
        action();
      } else {
        queuedActionRef.current = action;
        setIsWaitingForReady(true);
      }
    },
    [shouldQueue]
  );

  return {
    executeOrQueue,
    isWaitingForReady: shouldQueue && isWaitingForReady
  };
}
